import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  Truck, 
  Calculator,
  Wallet,
  FileText,
  LogOut,
  Building2,
  User,
  Settings,
  Menu,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const Sidebar = ({ currentPage, setCurrentPage, user, onLogout }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [branding, setBranding] = useState({ company_name: 'Apparel Manager', logo_base64: null });

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/business-info`);
        if (r.data?.company_name) {
          setBranding({ company_name: r.data.company_name, logo_base64: r.data.logo_base64 });
          document.title = r.data.company_name;
        }
      } catch { /* ignore */ }
    };
    load();
    // Re-sync when a custom event fires (e.g., after admin saves settings)
    const handler = () => load();
    window.addEventListener('business-settings-updated', handler);
    return () => window.removeEventListener('business-settings-updated', handler);
  }, []);

  const getMenuItems = () => {
    const baseItems = [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'orders', label: 'Orders', icon: Package },
      { id: 'customers', label: 'Customers', icon: Users },
      { id: 'suppliers', label: 'Suppliers', icon: Truck },
      { id: 'accounting', label: 'Accounting', icon: Calculator },
      { id: 'payroll', label: 'Payroll', icon: Wallet },
      { id: 'documents', label: 'Documents', icon: FileText },
    ];

    // Add admin menu for admin users
    if (user.role === 'admin') {
      baseItems.push({ id: 'admin', label: 'Admin Panel', icon: Settings });
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  const handleMenuItemClick = (itemId) => {
    setCurrentPage(itemId);
    setIsMobileMenuOpen(false); // Close mobile menu when item is selected
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          variant="ghost"
          size="sm"
          className="bg-white/90 text-gray-900 hover:bg-white shadow-lg"
        >
          {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar fixed left-0 top-0 h-full w-64 text-white z-50 transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
      <div className="p-6">
        {/* Logo */}
        <div className="flex items-center space-x-3 mb-8">
          {branding.logo_base64 ? (
            <img src={branding.logo_base64} alt="Logo" className="h-10 w-10 rounded-lg object-contain bg-white p-1" data-testid="sidebar-logo" />
          ) : (
            <div className="p-2 rounded-lg bg-white/20">
              <Building2 className="h-6 w-6" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-bold truncate" data-testid="sidebar-company-name">{branding.company_name}</h1>
            <p className="text-sm opacity-80">Business Management</p>
          </div>
        </div>

        {/* User Info */}
        <div className="bg-white/10 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-white/20">
              <User className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium">{user.username}</p>
              <p className="text-sm opacity-80 capitalize">{user.role}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleMenuItemClick(item.id)}
                className={`sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
                  currentPage === item.id ? 'active' : ''
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Logout Button */}
      <div className="absolute bottom-6 left-6 right-6">
        <Button
          onClick={onLogout}
          variant="ghost"
          className="w-full justify-start text-white hover:bg-white/10"
        >
          <LogOut className="h-5 w-5 mr-3" />
          Logout
        </Button>
      </div>
    </div>
    </>
  );
};

export default Sidebar;